import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck, Menu, X } from "lucide-react";
import { contact } from "../mock";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header className="w-full sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-neutral-900 text-neutral-300 text-xs hidden md:block">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Phone size={14} className="text-orange-500" /> {contact.phone}
            </a>
            <a href={`mailto:${contact.emails[0]}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Mail size={14} className="text-orange-500" /> {contact.emails[0]}
            </a>
            <span className="flex items-center gap-2">
              <MapPin size={14} className="text-orange-500" /> {contact.location}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <ShieldCheck size={14} className="text-orange-500" />
            <span className="tracking-wider">ISO 9001:2015 CERTIFIED</span>
            <span className="text-orange-500">•</span>
            <span className="tracking-wider">CE • RoHS COMPLIANT</span>
            <span className="ml-3"><LanguageSwitcher variant="dark" /></span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`bg-white transition-shadow ${scrolled ? "shadow-md" : ""}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex flex-col">
            <span className="text-[10px] tracking-[0.2em] text-orange-600 font-semibold">AN ISO 9001:2015 CERTIFIED COMPANY</span>
            <span className="font-display text-2xl md:text-[28px] font-bold text-neutral-900 leading-tight">
              ESS BEE HEATERS <span className="text-orange-500">PVT. LTD.</span>
            </span>
            <span className="h-[3px] w-12 bg-orange-500 mt-1" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wider uppercase relative pb-1 transition-colors ${
                    isActive ? "text-orange-500" : "text-neutral-800 hover:text-orange-500"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span className={`absolute left-0 right-0 -bottom-1 h-[3px] bg-orange-500 origin-left transition-transform duration-300 ${isActive ? "scale-x-100" : "scale-x-0"}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" className="hidden md:inline-block">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold tracking-wider uppercase px-6 py-6 rounded-full">
                Get Quote
              </Button>
            </Link>
            <button
              className="lg:hidden p-2 text-neutral-800"
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <span className="md:hidden"><LanguageSwitcher variant="light" /></span>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-neutral-200 bg-white">
            <nav className="flex flex-col px-6 py-4 gap-3">
              {navLinks.map(l => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `text-sm font-semibold tracking-wider uppercase py-2 ${isActive ? "text-orange-500" : "text-neutral-800"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white w-full rounded-full mt-2">Get Quote</Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
